import { useState, useCallback } from 'react';
import toast from 'react-hot-toast';

/**
 * Validações para os formulários da roda
 */
export const useFormValidation = () => {
  const [customFieldsErrors, setCustomFieldsErrors] = useState<Record<string, string>>({});

  const validateWhatsApp = useCallback((whatsapp: string): boolean => {
    // Remover caracteres não numéricos
    const digits = whatsapp.replace(/\D/g, '');

    // Verificar se tem pelo menos 10 dígitos (DDD + número)
    if (digits.length < 10 || digits.length > 13) {
      toast.error('WhatsApp inválido');
      return false;
    }

    // Extrair apenas os últimos 10 ou 11 dígitos (sem código do país)
    const phoneDigits = digits.slice(-11);

    // Verificar se não é um número com todos os dígitos iguais
    const allSame = /^(\d)\1+$/.test(phoneDigits);
    if (allSame) {
      toast.error('WhatsApp inválido');
      return false;
    }

    // Verificar se o DDD é válido (11 a 99)
    const ddd = parseInt(phoneDigits.substring(0, 2), 10);
    if (ddd < 11 || ddd > 99) {
      toast.error('DDD inválido');
      return false;
    }

    // Se for celular (11 dígitos), o terceiro dígito deve ser 9
    if (phoneDigits.length === 11) {
      const thirdDigit = phoneDigits.charAt(2);
      if (thirdDigit !== '9') {
        toast.error('Número de celular inválido');
        return false;
      }
    }

    return true;
  }, []);

  const validateBirthdate = useCallback((birthdate: string): boolean => {
    if (!birthdate) return true;

    const parts = birthdate.split('/');
    if (parts.length !== 3) {
      toast.error('Data de nascimento inválida');
      return false;
    }

    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);

    // Validar se são números válidos
    if (isNaN(day) || isNaN(month) || isNaN(year)) {
      toast.error('Data de nascimento inválida');
      return false;
    }

    // Validar ranges básicos
    if (day < 1 || day > 31 || month < 0 || month > 11 || year < 1900) {
      toast.error('Data de nascimento inválida');
      return false;
    }

    const date = new Date(year, month, day);
    
    // Verificar se a data é válida (ex: 31/02 não é válido)
    const isValidDate = 
      date.getDate() === day &&
      date.getMonth() === month &&
      date.getFullYear() === year;

    if (!isValidDate) {
      toast.error('Data de nascimento inválida');
      return false;
    }

    // Verificar se a data não é no futuro
    const now = new Date();
    if (date > now) {
      toast.error('Data de nascimento não pode ser no futuro');
      return false;
    }

    // Verificar idade mínima de 13 anos
    const age = now.getFullYear() - date.getFullYear();
    const monthDiff = now.getMonth() - date.getMonth();
    const dayDiff = now.getDate() - date.getDate();
    const actualAge = monthDiff < 0 || (monthDiff === 0 && dayDiff < 0) ? age - 1 : age;

    if (actualAge < 13) {
      toast.error('Você deve ter pelo menos 13 anos para participar');
      return false;
    }

    // Verificar idade máxima de 120 anos
    if (actualAge > 120) {
      toast.error('Data de nascimento inválida');
      return false;
    }

    return true;
  }, []);

  const validateCustomFields = useCallback((
    customFields: any[],
    customFieldsData: Record<string, any>
  ): boolean => {
    if (!customFields || customFields.length === 0) {
      return true;
    }

    const errors: Record<string, string> = {};
    let isValid = true;

    customFields.forEach(field => {
      const value = customFieldsData[field.id];

      if (field.required && (!value || value === '')) {
        errors[field.id] = `${field.label} é obrigatório`;
        isValid = false;
        return;
      }

      if (value) {
        switch (field.type) {
          case 'email':
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
              errors[field.id] = 'Email inválido';
              isValid = false;
            }
            break;
          case 'phone':
            const phoneDigits = value.replace(/\D/g, '');
            if (phoneDigits.length < 10) {
              errors[field.id] = 'Telefone inválido';
              isValid = false;
            }
            break;
          case 'date':
            if (!/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
              errors[field.id] = 'Data inválida (use DD/MM/AAAA)';
              isValid = false;
            }
            break;
          case 'number':
            if (isNaN(Number(value))) {
              errors[field.id] = 'Deve ser um número válido';
              isValid = false;
            }
            break;
        }
      }
    });

    setCustomFieldsErrors(errors);
    return isValid;
  }, []);

  const normalizeWhatsApp = useCallback((whatsapp: string): string => {
    let normalized = whatsapp.replace(/\D/g, '');
    if (normalized.length === 10 || normalized.length === 11) {
      normalized = '+55' + normalized;
    } else if (!normalized.startsWith('+55')) {
      normalized = '+55' + normalized;
    }
    return normalized;
  }, []);

  return {
    validateWhatsApp,
    validateBirthdate,
    validateCustomFields,
    normalizeWhatsApp,
    customFieldsErrors,
    setCustomFieldsErrors
  };
};
